import { useState, useMemo } from 'react';
import { DataTable, Filters, Box, Link, Button, Popover, ActionList, Icon, Modal, TextContainer, Select, Tooltip, Badge } from '@shopify/polaris';
import { formatToCurrency, getTableFooter, patchRequest } from '../../../shared/components/functions';
import { addFilters, applyFilters, removeFilter, handleFiltersClearAll, handleFilterChange } from '../../../shared/components/listFilter';
import InvoicePdfPreviewButton from '../../../shared/components/InvoicePdfPreviewButton';
import { DatabaseIcon } from '@shopify/polaris-icons';
import { useLookupStore } from '../../../context/useStore';

export default function CostBreakdownTable({ items, page, setPage, filters, setFilters, navigate, filterLoading, limit, setLimit, purchasetypes, refreshData }) {
  const [activePopover, setActivePopover] = useState(false);
  const [showGrouped, setShowGrouped] = useState(false);
  const [editingBusinessUnitId, setEditingBusinessUnitId] = useState(null);
  const businessunits = useLookupStore((state) => state.businessunits);

  // Filter out irrelevant filters for this view
  const relevantFilters = ['cost', 'costType', 'dateInFrom', 'dateInTo', 'businessUnit'];
  const filteredFiltersState = Object.keys(filters)
    .filter((key) => relevantFilters.includes(key))
    .reduce((obj, key) => {
      obj[key] = filters[key];
      return obj;
    }, {});

  const purchaseTypeOptions = useMemo(() => {
    if (!purchasetypes) return [];
    return purchasetypes.sort((a, b) => a.BestellingType.localeCompare(b.BestellingType)).map((pt) => ({ label: pt.BestellingType, value: pt.BestellingTypeID.toString() }));
  }, [purchasetypes]);

  const businessUnitOptions = useMemo(() => {
    return [
      { label: 'No BU', value: '' },
      ...businessunits
        .filter((bu) => bu.IsActive)
        .sort((a, b) => (a.SortOrder > b.SortOrder ? 1 : a.SortOrder < b.SortOrder ? -1 : a.Name.localeCompare(b.Name)))
        .map((bu) => ({ label: `${bu.Code} (${bu.Name})`, value: String(bu.BusinessUnitID) }))
    ];
  }, [businessunits]);

  const handleCostTypeChange = async (id, newTypeID) => {
    // Optimistic update could be done here if 'items' was local state,
    // but since it's passed from parent, we'll just trigger a refresh.
    // Ideally we would update the item in the list directly for speed.
    if (!id) return;

    await patchRequest(
      `/api/invoice-approvals/${id}/cost-type`,
      { FactuurTypeID: newTypeID },
      () => {}, // Success toast
      (err) => console.error('Failed to update cost type', err)
    );

    if (refreshData) refreshData();
  };

  const handleBusinessUnitChange = async (id, newBusinessUnitId) => {
    if (!id) return;
    const normalizedBusinessUnitId = newBusinessUnitId === '' ? null : Number(newBusinessUnitId);

    await patchRequest(
      `/api/invoice-approvals/${id}/business-unit`,
      { BusinessUnitID: normalizedBusinessUnitId },
      () => {},
      () => console.error('Failed to update business unit')
    );

    if (refreshData) refreshData();
  };

  const handleBusinessUnitSelect = async (id, value) => {
    await handleBusinessUnitChange(id, value);
    setEditingBusinessUnitId(null);
  };

  const groupedItems = useMemo(() => {
    if (!showGrouped || !items) return [];

    const groups = items.reduce((acc, item) => {
      const supplierName = item.supplier_name || 'Unknown Supplier';
      if (!acc[supplierName]) {
        acc[supplierName] = {
          supplier_name: supplierName,
          LeverancierNummer: item.LeverancierNummer,
          total: 0,
          count: 0,
          cost_types: new Set(),
          business_units: new Set()
        };
      }
      acc[supplierName].total += item.total || 0;
      acc[supplierName].count += 1;
      if (item.cost_type) acc[supplierName].cost_types.add(item.cost_type);
      if (item.business_unit_code) acc[supplierName].business_units.add(item.business_unit_code);
      return acc;
    }, {});

    return Object.values(groups).sort((a, b) => b.total - a.total);
  }, [items, showGrouped]);

  const rows = showGrouped
    ? groupedItems.map((item) => [
        <Link removeUnderline url={'/companies/' + item.LeverancierNummer}>
          {item.supplier_name}
        </Link>,
        item.count,
        Array.from(item.business_units).join(', '),
        Array.from(item.cost_types).join(', '),
        formatToCurrency(item.total)
      ])
    : Array.isArray(items)
    ? items.map((item) => [
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <Link removeUnderline url={'/invoices_in/' + item.faktuurinvolgnummer}>
            {item.FactuurInNummer}
          </Link>
          <div style={{ marginTop: '-5px', marginBottom: '-5px' }}>
            <InvoicePdfPreviewButton s3ObjectKeys={item.S3ObjectKeys} buttonLabel="View PDF" buttonSize="slim" buttonEmoji="👀" modalTitle="Invoice PDF Preview" />
          </div>
        </div>,
        item.date_in ? String(item.date_in).substring(0, 10) : '',
        <Link removeUnderline url={'/companies/' + item.LeverancierNummer}>
          {item.supplier_name}
        </Link>,
        editingBusinessUnitId === item.ProjectFactuurInID ? (
          <div style={{ width: '180px', transform: 'scale(0.8)', transformOrigin: 'top left', marginTop: '-10px', marginBottom: '-10px' }}>
            <Select
              label="Business Unit"
              labelHidden
              options={businessUnitOptions}
              value={item.BusinessUnitID != null ? String(item.BusinessUnitID) : ''}
              onChange={(newValue) => {
                handleBusinessUnitSelect(item.ProjectFactuurInID, newValue);
              }}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setEditingBusinessUnitId(item.ProjectFactuurInID)}
            style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer' }}
            title="Click to change business unit"
          >
            {item.business_unit_code ? <Badge>{item.business_unit_code}</Badge> : <Badge tone="warning">BU ?</Badge>}
          </button>
        ),
        <div style={{ width: '140px', transform: 'scale(0.8)', transformOrigin: 'top left', marginTop: '-10px', marginBottom: '-10px' }}>
          <Select
            label="Cost Type"
            labelHidden
            options={purchaseTypeOptions}
            value={item.FactuurTypeID ? item.FactuurTypeID.toString() : ''}
            onChange={(newValue) => handleCostTypeChange(item.ProjectFactuurInID, newValue)}
          />
        </div>,
        item.note ? (
          <Tooltip content={item.note}>
            <span>{item.note.length > 25 ? `${item.note.slice(0, 25)}…` : item.note}</span>
          </Tooltip>
        ) : (
          ''
        ),
        item.approver || '',
        formatToCurrency(item.total)
      ])
    : [];

  const totals = showGrouped
    ? items.length > 0
      ? ['', '', '', '', formatToCurrency(items.reduce((p, c) => p + (c.total || 0), 0))]
      : null
    : items.length > 0
    ? ['', '', '', '', '', '', '', formatToCurrency(items.reduce((p, c) => p + (c.total || 0), 0))]
    : null;

  const toggleGroupedView = () => {
    setShowGrouped(!showGrouped);
    setActivePopover(false);
  };

  const activator = (
    <Button onClick={() => setActivePopover(!activePopover)} disclosure icon={DatabaseIcon}>
      View Options
    </Button>
  );

  return (
    <Box>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
        <div style={{ flexGrow: 1 }}>
          <Filters
            queryValue={filters.q.value}
            filters={addFilters(filteredFiltersState, setFilters, navigate, setPage)}
            appliedFilters={applyFilters(filteredFiltersState, setFilters, navigate, setPage)}
            loading={filterLoading}
            onQueryChange={(e) => handleFilterChange(e, filters, setFilters, 'q', navigate, setPage)}
            onQueryClear={() => removeFilter('q', filters, setFilters, navigate, setPage)}
            queryPlaceholder="Search supplier or invoice number..."
            onClearAll={() => handleFiltersClearAll(filters, setFilters, navigate, setPage)}
          />
        </div>
        <div style={{ marginTop: '8px' }}>
          <Popover active={activePopover} activator={activator} onClose={() => setActivePopover(false)}>
            <ActionList
              items={[
                {
                  content: showGrouped ? 'Show Individual Invoices' : 'Group by Supplier',
                  onAction: toggleGroupedView,
                  icon: DatabaseIcon
                }
              ]}
            />
          </Popover>
        </div>
      </div>

      {showGrouped ? (
        <DataTable
          verticalAlign="middle"
          stickyHeader
          increasedTableDensity
          columnContentTypes={['text', 'numeric', 'text', 'text', 'numeric']}
          headings={[<b>Supplier</b>, <b>Invoice Count</b>, <b>Business Units</b>, <b>Cost Types</b>, <b>Total Value</b>]}
          rows={rows}
          totals={totals}
          showTotalsInFooter
          footerContent={`Displaying ${groupedItems.length} suppliers based on current page selection`}
        />
      ) : (
        <DataTable
          verticalAlign="middle"
          stickyHeader
          increasedTableDensity
          columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'numeric']}
          headings={[<b>Invoice Nr</b>, <b>Date In</b>, <b>Supplier</b>, <b>Business Unit</b>, <b>Cost Type</b>, <b>Note</b>, <b>Approved By</b>, <b>Total</b>]}
          rows={rows}
          totals={totals}
          showTotalsInFooter
          footerContent={getTableFooter(rows, page, setPage, 'cost entries', { rowsPerPage: limit, setRowsPerPage: setLimit })}
        />
      )}
    </Box>
  );
}
